import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useLocalData } from "../hooks/useLocalData";
import { useAuth } from "../hooks/useAuth";
import { generateNoteFromTranscript } from "../services/aiService";
import {
  saveRecoveryData,
  getRecoveryData,
} from "../services/sessionRecoveryService";
import { CheckCircle, ClipboardList, AlertTriangle, RefreshCw, Shield, FileText } from "lucide-react";

function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function hydratedStateFromRecovery(row) {
  if (!row) return null;
  return {
    sessionId: row.id,
    transcript: row.transcript || "",
    individualName: row.individualName,
    individualId: row.individualId,
    dob: row.dob,
    locationType: row.locationType,
    location: row.location,
    serviceType: row.serviceType || "ICM Weekly Visit",
    knownMedications: row.knownMedications || [],
    knownDiagnoses: row.knownDiagnoses || [],
    duration: row.duration || 1800,
    sessionData: {
      duration: row.duration || 1800,
      flaggedSegments: row.flaggedNotes || [],
    },
    extractedFacts: row.extractedFacts || [],
    gpsLocation: row.gpsLocation || null,
    draftNoteId: row.draftNoteId || null,
    appointmentId: row.appointmentId || null,
  };
}

export default function ProcessingPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { addNote, updateNote, recordDraftSession } = useLocalData();
  const { profile } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [isRetrying, setIsRetrying] = useState(false);
  const [hydrated, setHydrated] = useState(false);
  const startTimeRef = useRef(Date.now());
  const stateRef = useRef(location.state); // Keep state across retries
  const wakeLockRef = useRef(null);

  const steps = [
    "Transcribing audio",
    "Identifying interventions",
    "Applying billing codes",
    "Structuring clinical note",
  ];

  const extractedFacts = stateRef.current?.extractedFacts || [];

  const runGeneration = useCallback(async () => {
    setError(null);
    startTimeRef.current = Date.now();

    const sessionData = stateRef.current?.sessionData;
    const rawTranscript = stateRef.current?.transcript || "";

    setCurrentStep(1);
    await delay(400);

    setCurrentStep(2);
    await delay(400);

    setCurrentStep(3);

    let transcriptText = rawTranscript;
    if (sessionData?.transcript?.length > 0) {
      transcriptText = sessionData.transcript.map((t) => t.text).join(" ");
    }

    if (!transcriptText || transcriptText.trim().length < 10) {
      transcriptText = "No transcript available. Generate a template note for an ICM weekly visit.";
    }

    const flaggedNotes = sessionData?.flaggedSegments?.map((seg) => ({
      time: Math.floor(seg.startTime / 60) + ":" + String(seg.startTime % 60).padStart(2, "0"),
      text: seg.transcript,
    })) || [];

    // Include extracted facts in the generation context for better accuracy
    const factsContext = extractedFacts.length > 0
      ? extractedFacts.map((f) => `[${f.category}] ${f.text}`).join("\n")
      : "";

    if (factsContext) {
      transcriptText += `\n\nVERIFIED FACTS EXTRACTED DURING SESSION (use these as reference points):\n${factsContext}`;
    }

    try {
      const result = await generateNoteFromTranscript(transcriptText, {
        sessionId: stateRef.current?.sessionId,
        individualName: stateRef.current?.individualName || "Individual",
        dob: stateRef.current?.dob || "",
        date: new Date().toLocaleDateString(),
        locationType: stateRef.current?.locationType || "Out-of-Clinic",
        location: stateRef.current?.location || "Community",
        clinician: profile?.displayName
          ? `${profile.displayName}${profile.credentials ? `, ${profile.credentials}` : ""}${profile.employeeId ? ` (${profile.employeeId})` : ""}`
          : "ICM Staff",
        serviceType: stateRef.current?.serviceType || "ICM Weekly Visit",
        duration: stateRef.current?.duration || sessionData?.duration || 1800,
        flaggedNotes,
        knownMedications: stateRef.current?.knownMedications || [],
        knownDiagnoses: stateRef.current?.knownDiagnoses || [],
      });

      setCurrentStep(4);
      await delay(500);

      if (result.success) {
        const genTime = Math.round((Date.now() - startTimeRef.current) / 1000);
        navigate("/session/review", {
          state: {
            sessionId: stateRef.current?.sessionId,
            draftNoteId: stateRef.current?.draftNoteId || null,
            note: result.note,
            fromAI: true,
            duration: stateRef.current?.duration || sessionData?.duration || 1800,
            aiGenerationTime: genTime,
            individualName: stateRef.current?.individualName,
            individualId: stateRef.current?.individualId || null,
            serviceType: stateRef.current?.serviceType,
            extractedFacts,
            transcript: stateRef.current?.transcript || transcriptText,
            knownMedications: stateRef.current?.knownMedications || [],
            knownDiagnoses: stateRef.current?.knownDiagnoses || [],
            gpsLocation: stateRef.current?.gpsLocation || null,
          },
        });
      } else {
        throw new Error(result.error || "Note generation failed");
      }
    } catch (err) {
      console.error("Processing error:", err);
      setError(err.message);
      setRetryCount((prev) => prev + 1);
    }
  }, [navigate, extractedFacts]);

  // Hydrate state from IndexedDB if router state is incomplete (tab was reloaded
  // mid-flow). Also defensively persist the in-flight state so the recovery row
  // stays alive through generation.
  useEffect(() => {
    let cancelled = false;
    async function hydrate() {
      const incoming = location.state;
      const hasFullState = incoming && incoming.sessionId;

      if (hasFullState) {
        // Defensive write — ensures recovery row exists with status "processing"
        // even if ActiveSessionPage missed the save (e.g., page reload).
        await saveRecoveryData({
          id: incoming.sessionId,
          status: "processing",
          transcript: incoming.transcript,
          flaggedNotes: incoming.sessionData?.flaggedSegments || [],
          extractedFacts: incoming.extractedFacts || [],
          duration: incoming.duration || incoming.sessionData?.duration || 1800,
          lastSaved: Date.now(),
          individualName: incoming.individualName,
          individualId: incoming.individualId,
          dob: incoming.dob,
          locationType: incoming.locationType,
          location: incoming.location,
          serviceType: incoming.serviceType,
          knownMedications: incoming.knownMedications || [],
          knownDiagnoses: incoming.knownDiagnoses || [],
          gpsLocation: incoming.gpsLocation,
          draftNoteId: incoming.draftNoteId,
          appointmentId: incoming.appointmentId,
        });
        if (!cancelled) setHydrated(true);
        return;
      }

      // Router state lost — try to find a pending processing row in IndexedDB
      const rows = await getRecoveryData();
      const pending = rows
        .filter((r) => r.status === "processing")
        .sort((a, b) => (b.lastSaved || 0) - (a.lastSaved || 0))[0];

      if (pending) {
        stateRef.current = hydratedStateFromRecovery(pending);
        if (!cancelled) setHydrated(true);
      } else {
        // Nothing to process and nothing to recover — bounce out
        navigate("/dashboard", { replace: true });
      }
    }
    hydrate();
    return () => { cancelled = true; };
  }, [location.state, navigate]);

  // Keep the screen awake during generation to reduce the chance of
  // the tab being suspended by the OS.
  useEffect(() => {
    async function acquireWakeLock() {
      try {
        if ("wakeLock" in navigator) {
          wakeLockRef.current = await navigator.wakeLock.request("screen");
        }
      } catch (e) {
        // Best effort — some browsers/contexts will reject. Not fatal.
      }
    }
    acquireWakeLock();
    const onVisibility = () => {
      if (document.visibilityState === "visible") acquireWakeLock();
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      if (wakeLockRef.current) {
        try { wakeLockRef.current.release(); } catch (e) {}
        wakeLockRef.current = null;
      }
    };
  }, []);

  // Run generation once hydration is complete
  useEffect(() => {
    if (hydrated) runGeneration();
  }, [hydrated]); // eslint-disable-line

  const handleRetry = async () => {
    setIsRetrying(true);
    await runGeneration();
    setIsRetrying(false);
  };

  const handleSaveDraftAndExit = () => {
    const duration = stateRef.current?.duration || stateRef.current?.sessionData?.duration || 1800;
    const durationMin = Math.round(duration / 60);
    const billingUnits = Math.max(1, Math.ceil(durationMin / 15));
    const draftNoteId = stateRef.current?.draftNoteId;

    const draftData = {
      individualName: stateRef.current?.individualName || "Individual",
      date: new Date().toLocaleDateString(),
      serviceType: stateRef.current?.serviceType || "ICM Weekly Visit",
      location: stateRef.current?.location || "Community",
      locationType: stateRef.current?.locationType || "Out-of-Clinic",
      status: "draft",
      billingCode: "H0039",
      billingUnits: `${billingUnits} units`,
      duration: `${durationMin} min`,
      durationSeconds: duration,
      confidence: null,
      hasPriorityFlags: false,
      noteData: null,
      transcript: stateRef.current?.transcript || "",
      flaggedNotes: stateRef.current?.sessionData?.flaggedSegments || [],
      extractedFacts,
      dob: stateRef.current?.dob || "",
      knownMedications: stateRef.current?.knownMedications || [],
      knownDiagnoses: stateRef.current?.knownDiagnoses || [],
      generationFailed: true,
      failedAt: new Date().toISOString(),
      failureReason: error,
    };

    if (draftNoteId) {
      // Update the existing draft — don't create a duplicate
      updateNote(draftNoteId, draftData);
    } else {
      addNote(draftData);
      recordDraftSession({
        individualName: stateRef.current?.individualName || "Individual",
        duration,
      });
    }

    navigate("/dashboard");
  };

  // ===== ERROR STATE =====
  if (error) {
    return (
      <div className="min-h-screen bg-surface-primary flex flex-col items-center justify-center px-8">
        <div
          className="w-20 h-20 rounded-full flex items-center justify-center mb-6"
          style={{ background: "rgba(252,129,129,0.1)" }}
        >
          <AlertTriangle size={36} className="text-red-400" />
        </div>

        <h2 className="text-xl font-bold text-gray-100 mb-2 text-center">
          Generation Failed
        </h2>
        <p className="text-sm text-gray-500 text-center leading-relaxed max-w-xs mb-1">
          {error}
        </p>
        <p className="text-xs text-gray-600 text-center mb-4">
          Attempt {retryCount} of 3 · Your transcript is safe
        </p>

        {/* Safety net indicator */}
        <div
          className="w-full max-w-xs rounded-xl p-4 mb-5"
          style={{ background: "rgba(79,209,197,0.04)", border: "1px solid rgba(79,209,197,0.1)" }}
        >
          <div className="flex items-center gap-2 mb-2">
            <Shield size={14} className="text-teal-400" />
            <span className="text-xs font-bold text-teal-400">Data Protected</span>
          </div>
          <div className="text-[11px] text-gray-500 leading-relaxed">
            Your {Math.round((stateRef.current?.duration || 1800) / 60)}-minute transcript
            {extractedFacts.length > 0
              ? ` and ${extractedFacts.length} extracted facts are`
              : " is"
            } saved locally. Nothing has been lost.
          </div>
        </div>

        <div className="flex flex-col gap-2.5 w-full max-w-xs">
          {retryCount < 3 && (
            <button
              className="w-full py-3.5 rounded-xl text-white font-semibold text-sm flex items-center justify-center gap-2"
              style={{ background: "linear-gradient(135deg, #3182ce, #2b6cb0)" }}
              onClick={handleRetry}
              disabled={isRetrying}
            >
              <RefreshCw size={15} className={isRetrying ? "animate-spin" : ""} />
              {isRetrying ? "Retrying..." : `Retry Generation (${3 - retryCount} left)`}
            </button>
          )}

          <button
            className="w-full py-3.5 rounded-xl font-semibold text-sm flex items-center justify-center gap-2"
            style={{
              background: "rgba(236,201,75,0.06)",
              color: "#ecc94b",
              border: "1px solid rgba(236,201,75,0.15)",
            }}
            onClick={handleSaveDraftAndExit}
          >
            <FileText size={15} />
            Save as Draft — Generate Later
          </button>

          {extractedFacts.length > 0 && (
            <div className="mt-2">
              <div className="text-[11px] font-bold text-teal-400 tracking-wider uppercase mb-2">
                Extracted Facts Safety Net
              </div>
              <div
                className="rounded-xl p-3"
                style={{ background: "rgba(15,23,42,0.5)", border: "1px solid rgba(79,209,197,0.08)", maxHeight: 200, overflowY: "auto" }}
              >
                {extractedFacts.map((fact, i) => (
                  <div key={i} className="flex gap-2 py-1">
                    <span
                      className="text-[9px] font-bold px-1.5 py-0.5 rounded-full shrink-0 uppercase"
                      style={{
                        background: fact.category === "SAFETY" ? "rgba(252,129,129,0.15)" : "rgba(79,209,197,0.1)",
                        color: fact.category === "SAFETY" ? "#fc8181" : "#4fd1c5",
                      }}
                    >
                      {fact.category}
                    </span>
                    <span className="text-[11px] text-gray-400">{fact.text}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ===== PROCESSING STATE =====
  return (
    <div className="min-h-screen bg-surface-primary flex flex-col items-center justify-center px-8">
      <div className="relative mb-10">
        <div
          className="w-24 h-24 rounded-full"
          style={{
            border: "3px solid rgba(99,179,237,0.1)",
            borderTopColor: "#63b3ed",
            animation: "spin 1s linear infinite",
          }}
        />
        <div className="absolute inset-0 flex items-center justify-center">
          <ClipboardList size={32} className="text-gray-300" />
        </div>
      </div>

      <h2 className="text-2xl font-bold text-gray-100 mb-2 text-center">
        Generating Clinical Note
      </h2>
      <p className="text-sm text-gray-500 text-center leading-relaxed max-w-xs">
        Analyzing session transcript, applying ICM documentation standards and billable language...
      </p>

      <div className="w-full max-w-xs mt-8">
        {steps.map((step, i) => (
          <div key={i} className="flex items-center gap-2.5 py-2.5">
            {i < currentStep ? (
              <CheckCircle size={16} className="text-green-400 shrink-0" />
            ) : i === currentStep ? (
              <div
                className="w-4 h-4 rounded-full shrink-0"
                style={{
                  border: "2px solid rgba(99,179,237,0.3)",
                  borderTopColor: "#63b3ed",
                  animation: "spin 1s linear infinite",
                }}
              />
            ) : (
              <div className="w-4 h-4 rounded-full border border-gray-700 shrink-0" />
            )}
            <span
              className="text-sm font-medium"
              style={{
                color: i < currentStep ? "#68d391" : i === currentStep ? "#63b3ed" : "#4a5568",
              }}
            >
              {step}
            </span>
          </div>
        ))}
      </div>

      {/* Safety indicator */}
      <div className="flex items-center gap-1.5 mt-8">
        <Shield size={11} className="text-teal-400" />
        <span className="text-[10px] text-teal-400 font-medium">
          Transcript saved locally
          {extractedFacts.length > 0 && ` · ${extractedFacts.length} facts captured`}
        </span>
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}